import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus,
  X, MapPin, UsersRound, Clock, FileText,
  AlertTriangle, Wrench, Tag, CheckCircle2, Info
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});

const STYLES = `
  /* ── TOOLBAR ─────────────────────────────────── */
  .msc-toolbar {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 24px; flex-wrap: wrap;
  }
  .msc-nav {
    display: flex; align-items: center; gap: 4px;
  }
  .msc-nav-btn {
    width: 32px; height: 32px; border-radius: 8px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.55); cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.18s;
  }
  .msc-nav-btn:hover {
    background: rgba(255,255,255,0.09);
    color: rgba(255,255,255,0.85);
  }
  .msc-period-label {
    font-size: 15px; font-weight: 800;
    color: #F1F5F9; min-width: 180px; text-align: center;
    letter-spacing: -0.02em;
  }
  .msc-view-toggle {
    display: flex; border-radius: 9px; overflow: hidden;
    border: 1px solid rgba(255,255,255,0.08);
    margin-left: auto;
  }
  .msc-view-btn {
    padding: 7px 16px;
    font-size: 12px; font-weight: 700;
    color: rgba(255,255,255,0.38);
    background: rgba(255,255,255,0.04);
    border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.18s;
  }
  .msc-view-btn.active {
    background: linear-gradient(90deg, rgba(14,165,233,0.20), rgba(245,158,11,0.12));
    color: #E2E8F0;
  }
  .msc-today-btn {
    padding: 7px 14px; border-radius: 8px;
    font-size: 12px; font-weight: 700;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.45); cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.18s;
  }
  .msc-today-btn:hover {
    background: rgba(14,165,233,0.10);
    color: #38BDF8;
    border-color: rgba(14,165,233,0.22);
  }

  /* ── MONTHLY CALENDAR ────────────────────────── */
  .msc-month-grid {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px; overflow: hidden;
  }
  .msc-month-dow-row {
    display: grid; grid-template-columns: repeat(7, 1fr);
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .msc-month-dow {
    padding: 10px 0; text-align: center;
    font-size: 9px; font-weight: 700;
    letter-spacing: 0.16em; text-transform: uppercase;
    color: rgba(255,255,255,0.25);
    font-family: 'DM Mono', monospace;
  }
  .msc-month-days {
    display: grid; grid-template-columns: repeat(7, 1fr);
  }
  .msc-month-day {
    min-height: 110px; padding: 10px 10px 8px;
    border-right: 1px solid rgba(255,255,255,0.04);
    border-bottom: 1px solid rgba(255,255,255,0.04);
    cursor: pointer; transition: background 0.15s;
    position: relative;
  }
  .msc-month-day:nth-child(7n) { border-right: none; }
  .msc-month-day:hover { background: rgba(255,255,255,0.025); }
  .msc-month-day.other-month { opacity: 0.35; }
  .msc-month-day.today { background: rgba(14,165,233,0.05); }
  .msc-month-day.today .msc-day-num {
    background: linear-gradient(135deg, #0EA5E9, #F59E0B);
    color: #fff; border-radius: 6px;
    width: 24px; height: 24px;
    display: flex; align-items: center; justify-content: center;
  }
  .msc-day-num {
    font-size: 12px; font-weight: 700;
    color: rgba(255,255,255,0.45);
    margin-bottom: 6px; display: inline-block;
    width: 24px; height: 24px; line-height: 24px;
    text-align: center;
  }
  .msc-day-jobs { display: flex; flex-direction: column; gap: 3px; }
  .msc-day-job-pill {
    padding: 3px 7px; border-radius: 5px;
    font-size: 10px; font-weight: 600;
    white-space: nowrap; overflow: hidden;
    text-overflow: ellipsis; cursor: pointer;
    transition: opacity 0.15s;
  }
  .msc-day-job-pill:hover { opacity: 0.80; }
  .msc-pill-scheduled   { background: rgba(14,165,233,0.18);  color: #7DD3FC; }
  .msc-pill-in_progress { background: rgba(245,158,11,0.18);  color: #FDE68A; }
  .msc-pill-completed   { background: rgba(34,197,94,0.14);   color: #86EFAC; }
  .msc-pill-cancelled   { background: rgba(239,68,68,0.12);   color: #FCA5A5; text-decoration: line-through; }
  .msc-day-more {
    font-size: 9px; color: rgba(255,255,255,0.28);
    font-family: 'DM Mono', monospace;
    margin-top: 2px;
  }

  /* ── WEEKLY VIEW ─────────────────────────────── */
  .msc-week-grid {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px; overflow: hidden;
  }
  .msc-week-header {
    display: grid; grid-template-columns: repeat(7, 1fr);
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .msc-week-day-header {
    padding: 14px 12px; text-align: center;
    border-right: 1px solid rgba(255,255,255,0.04);
    cursor: pointer; transition: background 0.15s;
  }
  .msc-week-day-header:last-child { border-right: none; }
  .msc-week-day-header:hover { background: rgba(255,255,255,0.025); }
  .msc-week-day-header.today { background: rgba(14,165,233,0.06); }
  .msc-week-dow {
    font-size: 9px; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase;
    color: rgba(255,255,255,0.28);
    font-family: 'DM Mono', monospace;
    margin-bottom: 6px;
  }
  .msc-week-date {
    font-size: 20px; font-weight: 800;
    color: rgba(255,255,255,0.55); letter-spacing: -0.03em;
  }
  .msc-week-date.today-date {
    background: linear-gradient(135deg, #0EA5E9, #F59E0B);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .msc-week-cells {
    display: grid; grid-template-columns: repeat(7, 1fr);
    min-height: 300px;
  }
  .msc-week-cell {
    padding: 10px 8px;
    border-right: 1px solid rgba(255,255,255,0.04);
    cursor: pointer; transition: background 0.15s;
    min-height: 300px;
  }
  .msc-week-cell:last-child { border-right: none; }
  .msc-week-cell:hover { background: rgba(255,255,255,0.018); }
  .msc-week-job-card {
    background: rgba(14,165,233,0.10);
    border: 1px solid rgba(14,165,233,0.20);
    border-radius: 7px; padding: 7px 9px;
    margin-bottom: 6px; cursor: pointer;
    transition: all 0.15s;
  }
  .msc-week-job-card:hover {
    background: rgba(14,165,233,0.16);
    transform: translateY(-1px);
  }
  .msc-week-job-card.in_progress {
    background: rgba(245,158,11,0.10);
    border-color: rgba(245,158,11,0.20);
  }
  .msc-week-job-card.completed {
    background: rgba(34,197,94,0.08);
    border-color: rgba(34,197,94,0.16);
  }
  .msc-week-job-site {
    font-size: 11px; font-weight: 700;
    color: #E2E8F0; margin-bottom: 3px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .msc-week-job-team {
    font-size: 10px; color: rgba(255,255,255,0.35);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .msc-week-add-hint {
    font-size: 10px; color: rgba(255,255,255,0.14);
    text-align: center; padding: 20px 0;
    font-family: 'DM Mono', monospace;
  }

  /* ── JOB DETAIL PANEL ────────────────────────── */
  .msc-panel-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(4px);
    z-index: 200; animation: fadeIn 0.15s ease;
  }
  .msc-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 600px; max-width: 100vw;
    background: #0D1120;
    border-left: 1px solid rgba(255,255,255,0.08);
    box-shadow: -24px 0 80px rgba(0,0,0,0.50);
    z-index: 201; display: flex; flex-direction: column;
    animation: slideIn 0.22s cubic-bezier(0.4,0,0.2,1);
    overflow: hidden;
  }
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  .msc-panel-header {
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex; align-items: center; gap: 12px;
    flex-shrink: 0;
  }
  .msc-panel-title {
    font-size: 16px; font-weight: 800; color: #F1F5F9; flex: 1;
  }
  .msc-panel-sub {
    font-size: 11px; color: rgba(255,255,255,0.28); margin-top: 2px;
  }
  .msc-panel-tabs {
    display: flex; gap: 4px; padding: 0 24px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-shrink: 0;
  }
  .msc-panel-tab {
    padding: 12px 16px; font-size: 12px; font-weight: 700;
    color: rgba(255,255,255,0.35); cursor: pointer;
    border: none; background: none;
    border-bottom: 2px solid transparent;
    transition: all 0.18s; font-family: 'DM Sans', sans-serif;
  }
  .msc-panel-tab:hover { color: rgba(255,255,255,0.65); }
  .msc-panel-tab.active { color: #0EA5E9; border-bottom-color: #0EA5E9; }
  .msc-panel-body {
    flex: 1; overflow-y: auto; padding: 22px 24px;
  }

  /* Job info rows */
  .msc-info-row {
    display: flex; align-items: center; gap: 10px;
    padding: 11px 0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    font-size: 13px; color: rgba(255,255,255,0.55);
  }
  .msc-info-row:last-child { border-bottom: none; }
  .msc-info-label {
    width: 90px; flex-shrink: 0;
    font-size: 9px; font-weight: 700;
    color: rgba(255,255,255,0.22);
    font-family: 'DM Mono', monospace;
    text-transform: uppercase; letter-spacing: 0.12em;
  }

  /* Status selector */
  .msc-status-row {
    display: flex; gap: 8px; flex-wrap: wrap; margin-top: 16px;
  }
  .msc-status-chip {
    padding: 6px 14px; border-radius: 20px;
    font-size: 11px; font-weight: 700;
    font-family: 'DM Mono', monospace;
    cursor: pointer; border: 1px solid transparent;
    transition: all 0.18s;
  }
  .msc-status-chip-scheduled  { background: rgba(14,165,233,0.08);  color: #38BDF8;  border-color: rgba(14,165,233,0.18); }
  .msc-status-chip-in_progress{ background: rgba(245,158,11,0.08);  color: #FCD34D;  border-color: rgba(245,158,11,0.18); }
  .msc-status-chip-completed  { background: rgba(34,197,94,0.08);   color: #86EFAC;  border-color: rgba(34,197,94,0.18); }
  .msc-status-chip-cancelled  { background: rgba(239,68,68,0.08);   color: #FCA5A5;  border-color: rgba(239,68,68,0.18); }
  .msc-status-chip.selected   { opacity: 1; box-shadow: 0 0 0 2px currentColor; }
  .msc-status-chip:not(.selected) { opacity: 0.50; }

  /* ── DIAGRAM + NOTES TAB ─────────────────────── */
  .msc-diagram-wrap {
    position: relative; border-radius: 10px; overflow: hidden;
    border: 1px solid rgba(14,165,233,0.18);
    cursor: default; margin-bottom: 16px;
  }
  .msc-diagram-img {
    width: 100%; display: block; user-select: none;
  }
  .msc-note-pin {
    position: absolute; transform: translate(-50%, -50%);
    cursor: pointer; z-index: 10;
  }
  .msc-note-pin-dot {
    width: 24px; height: 24px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 800;
    font-family: 'DM Mono', monospace;
    box-shadow: 0 0 0 3px rgba(0,0,0,0.40), 0 4px 12px rgba(0,0,0,0.50);
    transition: transform 0.15s;
    color: #080C18;
  }
  .msc-note-pin:hover .msc-note-pin-dot { transform: scale(1.2); }
  .msc-note-pin-has-note { background: #F59E0B; }
  .msc-note-pin-no-note  { background: rgba(255,255,255,0.30); color: rgba(0,0,0,0.60); }
  .msc-note-pin-tooltip {
    position: absolute; bottom: 28px; left: 50%;
    transform: translateX(-50%);
    background: #0D1829;
    border: 1px solid rgba(14,165,233,0.25);
    border-radius: 7px; padding: 6px 10px;
    white-space: nowrap; font-size: 11px;
    color: #E2E8F0; font-weight: 600;
    pointer-events: none; opacity: 0;
    transition: opacity 0.15s;
    box-shadow: 0 4px 14px rgba(0,0,0,0.40);
    z-index: 20;
  }
  .msc-note-pin:hover .msc-note-pin-tooltip { opacity: 1; }

  /* Note items */
  .msc-note-item {
    padding: 14px; border-radius: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    margin-bottom: 8px;
  }
  .msc-note-item-header {
    display: flex; align-items: center; gap: 8px; margin-bottom: 8px;
  }
  .msc-note-type-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  }
  .msc-note-point-label {
    font-size: 11px; font-weight: 700;
    color: rgba(255,255,255,0.45);
    font-family: 'DM Mono', monospace;
    flex: 1;
  }
  .msc-note-text {
    font-size: 13px; color: rgba(255,255,255,0.65);
    line-height: 1.55;
  }

  /* Add note form */
  .msc-add-note-form {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(14,165,233,0.15);
    border-radius: 12px; padding: 16px;
    margin-bottom: 20px;
  }
  .msc-add-note-title {
    font-size: 11px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: rgba(14,165,233,0.60);
    font-family: 'DM Mono', monospace;
    margin-bottom: 12px;
  }
  .msc-note-type-grid {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 8px; margin-bottom: 12px;
  }
  .msc-note-type-btn {
    padding: 9px 12px; border-radius: 9px;
    font-size: 11px; font-weight: 700;
    cursor: pointer; border: 1px solid transparent;
    transition: all 0.18s; text-align: left;
    display: flex; align-items: center; gap: 7px;
    font-family: 'DM Sans', sans-serif;
  }
  .msc-note-type-btn.selected {
    box-shadow: 0 0 0 2px currentColor;
  }

  /* ── Form Element Specific Fixes ────────────────── */
  .md-input {
    color-scheme: dark; /* Yeh date/time k icon ko dark mode k mutabiq set kardega jisse clock white hojayega */
  }
  select.md-input {
    background-color: #0D1120;
    color: #F1F5F9;
  }
  select.md-input option {
    background-color: #0D1120;
    color: #F1F5F9;
  }
  /* Fallback in case color-scheme is not fully supported */
  input[type="date"]::-webkit-calendar-picker-indicator,
  input[type="time"]::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }
`;

// ── Helpers ────────────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// FIXED: Use local timezone date parts to construct string to prevent off-by-one errors from .toISOString()
const toDateStr = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth()    === b.getMonth()    &&
  a.getDate()     === b.getDate();

const statusPillClass = (s) => ({
  scheduled:   'msc-pill-scheduled',
  in_progress: 'msc-pill-in_progress',
  completed:   'msc-pill-completed',
  cancelled:   'msc-pill-cancelled',
}[s] || 'msc-pill-scheduled');

// ── Main Component ─────────────────────────────────────────────────
const MideastSchedule = () => {
  const today  = new Date();
  const [view,       setView]       = useState('month'); // 'month' | 'week'
  const [cursor,     setCursor]     = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [jobs,       setJobs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [sites,      setSites]      = useState([]);
  const [teams,      setTeams]      = useState([]);
  const [annotTypes, setAnnotTypes] = useState([]);

  // Create job modal
  const [showCreate,   setShowCreate]   = useState(false);
  const [createDate,   setCreateDate]   = useState('');
  const [createForm,   setCreateForm]   = useState({
    site_id: '', team_id: '', scheduled_time: '', notes: ''
  });
  const [creating,     setCreating]     = useState(false);
  const [createError,  setCreateError]  = useState('');

  // Job detail panel
  const [selectedJob,  setSelectedJob]  = useState(null);
  const [jobDetail,    setJobDetail]    = useState(null);
  const [jobLoading,   setJobLoading]   = useState(false);
  const [panelTab,     setPanelTab]     = useState('info');

  // Note form state
  const [notePointId,  setNotePointId]  = useState('');
  const [noteTypeId,   setNoteTypeId]   = useState('');
  const [noteText,     setNoteText]     = useState('');
  const [addingNote,   setAddingNote]   = useState(false);
  const [noteError,    setNoteError]    = useState('');

  // ── Date range for fetching ────────────────────────────────────
  const fetchRange = useCallback(() => {
    if (view === 'month') {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      return {
        from: toDateStr(new Date(y, m, 1)),
        to:   toDateStr(new Date(y, m + 1, 0)),
      };
    } else {
      // week: cursor is week start (Sunday)
      const end = new Date(cursor);
      end.setDate(end.getDate() + 6);
      return { from: toDateStr(cursor), to: toDateStr(end) };
    }
  }, [cursor, view]);

  // ── Fetch jobs ─────────────────────────────────────────────────
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const { from, to } = fetchRange();
    try {
      // Use week param for week view, month for month view
      const param = view === 'month'
        ? `month=${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2,'0')}`
        : `week=${from}`;
      const res = await axios.get(
        `${BASE}/mideast/jobs?${param}`,
        getAuthHeader()
      );
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error('[Schedule] fetch jobs error:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchRange, cursor, view]);

  // ── Fetch support data ─────────────────────────────────────────
  const fetchSupport = useCallback(async () => {
    try {
      const [sitesRes, teamsRes, annotRes] = await Promise.all([
        axios.get(`${BASE}/mideast/sites`,                          getAuthHeader()),
        axios.get(`${BASE}/mideast/teams`,                          getAuthHeader()),
        axios.get(`${BASE}/mideast/jobs/annotation-types/all`,      getAuthHeader()),
      ]);
      setSites(sitesRes.data.sites           || []);
      setTeams(teamsRes.data.teams           || []);
      setAnnotTypes(annotRes.data.annotation_types || []);
    } catch (err) {
      console.error('[Schedule] fetch support error:', err);
    }
  }, []);

  useEffect(() => { fetchJobs(); },   [fetchJobs]);
  useEffect(() => { fetchSupport(); }, [fetchSupport]);

  // ── Navigation ─────────────────────────────────────────────────
  const prev = () => {
    if (view === 'month') {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    } else {
      const d = new Date(cursor);
      d.setDate(d.getDate() - 7);
      setCursor(d);
    }
  };
  const next = () => {
    if (view === 'month') {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    } else {
      const d = new Date(cursor);
      d.setDate(d.getDate() + 7);
      setCursor(d);
    }
  };
  const goToday = () => {
    if (view === 'month') {
      setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    } else {
      const d = new Date(today);
      d.setDate(d.getDate() - d.getDay());
      setCursor(d);
    }
  };
  const switchView = (v) => {
    setView(v);
    if (v === 'week') {
      const d = new Date(today);
      d.setDate(d.getDate() - d.getDay());
      setCursor(d);
    } else {
      setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    }
  };

  // ── Period label ───────────────────────────────────────────────
  const periodLabel = view === 'month'
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : (() => {
        const end = new Date(cursor);
        end.setDate(end.getDate() + 6);
        return `${cursor.getDate()} ${MONTHS[cursor.getMonth()].slice(0,3)} — ${end.getDate()} ${MONTHS[end.getMonth()].slice(0,3)} ${end.getFullYear()}`;
      })();

  // ── Month calendar days ────────────────────────────────────────
  const monthDays = (() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1).getDay();
    const last  = new Date(y, m + 1, 0).getDate();
    const days  = [];
    // Pad from prev month
    const prevLast = new Date(y, m, 0).getDate();
    for (let i = first - 1; i >= 0; i--) {
      days.push({ date: new Date(y, m - 1, prevLast - i), current: false });
    }
    for (let d = 1; d <= last; d++) {
      days.push({ date: new Date(y, m, d), current: true });
    }
    // Pad to complete weeks
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({ date: new Date(y, m + 1, d), current: false });
    }
    return days;
  })();

  // ── Week days ──────────────────────────────────────────────────
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(cursor);
    d.setDate(d.getDate() + i);
    return d;
  });

  // ── Jobs for a specific date ───────────────────────────────────
  const jobsForDate = (date) =>
    jobs.filter(j => {
      const jd = new Date(j.scheduled_date);
      return isSameDay(jd, date);
    });

  // ── Open create modal ──────────────────────────────────────────
  const openCreate = (dateStr) => {
    setCreateDate(dateStr);
    setCreateForm({ site_id: '', team_id: '', scheduled_time: '', notes: '' });
    setCreateError('');
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!createForm.site_id || !createDate) {
      setCreateError('Site and date are required');
      return;
    }
    setCreating(true); setCreateError('');
    try {
      await axios.post(
        `${BASE}/mideast/jobs`,
        {
          site_id:        parseInt(createForm.site_id),
          team_id:        createForm.team_id ? parseInt(createForm.team_id) : null,
          scheduled_date: createDate,
          scheduled_time: createForm.scheduled_time || null,
          notes:          createForm.notes || null,
        },
        getAuthHeader()
      );
      setShowCreate(false);
      fetchJobs();
    } catch (err) {
      setCreateError(err?.response?.data?.error || 'Failed to create job');
    } finally {
      setCreating(false);
    }
  };

  // ── Open job detail ────────────────────────────────────────────
  const openJobDetail = useCallback(async (job, e) => {
    e?.stopPropagation();
    setSelectedJob(job);
    setJobDetail(null);
    setPanelTab('info');
    setNotePointId(''); setNoteTypeId(''); setNoteText('');
    setJobLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/mideast/jobs/${job.id}`,
        getAuthHeader()
      );
      setJobDetail(res.data);
    } catch (err) {
      console.error('[Schedule] job detail error:', err);
    } finally {
      setJobLoading(false);
    }
  }, []);

  // ── Update job status ──────────────────────────────────────────
  const updateStatus = async (status) => {
    try {
      await axios.patch(
        `${BASE}/mideast/jobs/${selectedJob.id}/status`,
        { status }, getAuthHeader()
      );
      setSelectedJob(j => ({ ...j, status }));
      setJobDetail(d => ({ ...d, job: { ...d.job, status } }));
      fetchJobs();
    } catch (err) {
      console.error('[Schedule] status update error:', err);
    }
  };

  // ── Add assignment note ────────────────────────────────────────
  const handleAddNote = async () => {
    if (!noteText.trim()) { setNoteError('Note text is required'); return; }
    setAddingNote(true); setNoteError('');
    try {
      await axios.post(
        `${BASE}/mideast/jobs/${selectedJob.id}/notes`,
        {
          point_id:           notePointId ? parseInt(notePointId) : null,
          annotation_type_id: noteTypeId  ? parseInt(noteTypeId)  : null,
          note:               noteText,
        },
        getAuthHeader()
      );
      setNoteText(''); setNotePointId(''); setNoteTypeId('');
      // Refresh
      const res = await axios.get(
        `${BASE}/mideast/jobs/${selectedJob.id}`,
        getAuthHeader()
      );
      setJobDetail(res.data);
    } catch (err) {
      setNoteError(err?.response?.data?.error || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  // ── Delete note ────────────────────────────────────────────────
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(
        `${BASE}/mideast/jobs/${selectedJob.id}/notes/${noteId}`,
        getAuthHeader()
      );
      const res = await axios.get(
        `${BASE}/mideast/jobs/${selectedJob.id}`,
        getAuthHeader()
      );
      setJobDetail(res.data);
    } catch (err) {
      alert('Failed to delete note');
    }
  };

  return (
    <>
      <style>{STYLES}</style>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <div className="md-page-title">Schedule</div>
        <div className="md-page-sub">Plan and manage Mideast job assignments</div>
      </div>

      {/* Toolbar */}
      <div className="msc-toolbar">
        <div className="msc-nav">
          <button className="msc-nav-btn" onClick={prev}>
            <ChevronLeft size={15}/>
          </button>
          <span className="msc-period-label">{periodLabel}</span>
          <button className="msc-nav-btn" onClick={next}>
            <ChevronRight size={15}/>
          </button>
        </div>
        <button className="msc-today-btn" onClick={goToday}>Today</button>
        <button
          className="md-btn md-btn-primary"
          onClick={() => openCreate(toDateStr(today))}
        >
          <Plus size={14}/> Schedule Job
        </button>
        <div className="msc-view-toggle">
          <button
            className={`msc-view-btn${view === 'month' ? ' active' : ''}`}
            onClick={() => switchView('month')}
          >
            Month
          </button>
          <button
            className={`msc-view-btn${view === 'week' ? ' active' : ''}`}
            onClick={() => switchView('week')}
          >
            Week
          </button>
        </div>
      </div>

      {/* ── MONTH VIEW ────────────────────────────────────────── */}
      {view === 'month' && (
        <div className="msc-month-grid">
          <div className="msc-month-dow-row">
            {DAYS.map(d => (
              <div key={d} className="msc-month-dow">{d}</div>
            ))}
          </div>
          <div className="msc-month-days">
            {monthDays.map(({ date, current }, idx) => {
              const dayJobs  = jobsForDate(date);
              const isToday  = isSameDay(date, today);
              return (
                <div
                  key={idx}
                  className={`msc-month-day${!current ? ' other-month' : ''}${isToday ? ' today' : ''}`}
                  onClick={() => openCreate(toDateStr(date))}
                >
                  <div className="msc-day-num">{date.getDate()}</div>
                  <div className="msc-day-jobs">
                    {dayJobs.slice(0, 3).map(job => (
                      <div
                        key={job.id}
                        className={`msc-day-job-pill ${statusPillClass(job.status)}`}
                        onClick={e => openJobDetail(job, e)}
                        title={job.site_name}
                      >
                        {job.site_name}
                      </div>
                    ))}
                    {dayJobs.length > 3 && (
                      <div className="msc-day-more">+{dayJobs.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── WEEK VIEW ─────────────────────────────────────────── */}
      {view === 'week' && (
        <div className="msc-week-grid">
          <div className="msc-week-header">
            {weekDays.map((date, i) => {
              const isToday = isSameDay(date, today);
              return (
                <div
                  key={i}
                  className={`msc-week-day-header${isToday ? ' today' : ''}`}
                  onClick={() => openCreate(toDateStr(date))}
                >
                  <div className="msc-week-dow">{DAYS[date.getDay()]}</div>
                  <div className={`msc-week-date${isToday ? ' today-date' : ''}`}>
                    {date.getDate()}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="msc-week-cells">
            {weekDays.map((date, i) => {
              const dayJobs = jobsForDate(date);
              return (
                <div
                  key={i}
                  className="msc-week-cell"
                  onClick={() => openCreate(toDateStr(date))}
                >
                  {dayJobs.length === 0 ? (
                    <div className="msc-week-add-hint">+ add</div>
                  ) : dayJobs.map(job => (
                    <div
                      key={job.id}
                      className={`msc-week-job-card ${job.status}`}
                      onClick={e => openJobDetail(job, e)}
                    >
                      <div className="msc-week-job-site">{job.site_name}</div>
                      <div className="msc-week-job-team">
                        {job.team_name || 'Unassigned'}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── JOB DETAIL PANEL ──────────────────────────────────── */}
      {selectedJob && (
        <>
          <div className="msc-panel-overlay" onClick={() => setSelectedJob(null)}/>
          <div className="msc-panel">

            <div className="msc-panel-header">
              <div style={{
                width: 40, height: 40, borderRadius: 11,
                background: 'rgba(14,165,233,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#38BDF8', flexShrink: 0
              }}>
                <CalendarDays size={18}/>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="msc-panel-title">
                  {jobDetail?.job?.site_name || selectedJob.site_name}
                </div>
                <div className="msc-panel-sub">
                  {new Date(selectedJob.scheduled_date).toLocaleDateString('en-GB', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </div>
              </div>
              <button className="md-modal-close" onClick={() => setSelectedJob(null)}>
                <X size={18}/>
              </button>
            </div>

            <div className="msc-panel-tabs">
              {[
                { id: 'info',  label: 'Job Info'  },
                { id: 'notes', label: 'Assignment Notes' },
              ].map(tab => (
                <button
                  key={tab.id}
                  className={`msc-panel-tab${panelTab === tab.id ? ' active' : ''}`}
                  onClick={() => setPanelTab(tab.id)}
                >
                  {tab.label}
                  {tab.id === 'notes' && jobDetail?.notes?.length > 0 && (
                    <span style={{
                      marginLeft: 6, background: 'rgba(245,158,11,0.20)',
                      color: '#FCD34D', borderRadius: 10,
                      padding: '1px 6px', fontSize: 9, fontFamily: 'DM Mono'
                    }}>
                      {jobDetail.notes.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="msc-panel-body">
              {jobLoading ? (
                <>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="md-skeleton"
                      style={{ height: 16, marginBottom: 14, width: `${55 + i * 10}%` }}/>
                  ))}
                </>
              ) : jobDetail ? (
                <>
                  {/* ── INFO TAB ── */}
                  {panelTab === 'info' && (
                    <>
                      <div style={{ marginBottom: 20 }}>
                        {[
                          { label: 'Site',    value: jobDetail.job.site_name,   icon: <MapPin size={13}/> },
                          { label: 'Client',  value: jobDetail.job.client_name, icon: <FileText size={13}/> },
                          { label: 'Team',    value: jobDetail.job.team_name || 'Unassigned', icon: <UsersRound size={13}/> },
                          { label: 'Time',    value: jobDetail.job.scheduled_time || '—', icon: <Clock size={13}/> },
                        ].map(({ label, value, icon }) => (
                          <div key={label} className="msc-info-row">
                            <span className="msc-info-label">{label}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ color: 'rgba(14,165,233,0.60)' }}>{icon}</span>
                              {value}
                            </span>
                          </div>
                        ))}
                        {jobDetail.job.notes && (
                          <div className="msc-info-row">
                            <span className="msc-info-label">Notes</span>
                            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                              {jobDetail.job.notes}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Team members */}
                      {jobDetail.members?.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
                            textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)',
                            fontFamily: 'DM Mono', marginBottom: 10,
                          }}>
                            Team Members
                          </div>
                          {jobDetail.members.map(m => (
                            <div key={m.id} style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 0',
                              borderBottom: '1px solid rgba(255,255,255,0.04)',
                            }}>
                              <div style={{
                                width: 28, height: 28, borderRadius: 7,
                                background: m.role === 'lead'
                                  ? 'rgba(245,158,11,0.14)' : 'rgba(14,165,233,0.10)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 700, flexShrink: 0,
                                color: m.role === 'lead' ? '#FCD34D' : '#38BDF8',
                              }}>
                                {`${m.firstname?.[0]||''}${m.lastname?.[0]||''}`.toUpperCase()}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#E2E8F0' }}>
                                  {m.firstname} {m.lastname}
                                </div>
                              </div>
                              <span style={{
                                fontSize: 9, fontWeight: 700,
                                color: m.role === 'lead' ? '#FCD34D' : '#38BDF8',
                                fontFamily: 'DM Mono', letterSpacing: '0.08em',
                              }}>
                                {m.role.toUpperCase()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Status */}
                      <div>
                        <div style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
                          textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)',
                          fontFamily: 'DM Mono', marginBottom: 10,
                        }}>
                          Update Status
                        </div>
                        <div className="msc-status-row">
                          {['scheduled','in_progress','completed','cancelled'].map(s => (
                            <button
                              key={s}
                              className={`msc-status-chip msc-status-chip-${s}${jobDetail.job.status === s ? ' selected' : ''}`}
                              onClick={() => updateStatus(s)}
                            >
                              {s.replace('_',' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── NOTES TAB ── */}
                  {panelTab === 'notes' && (
                    <>
                      {/* Add note form */}
                      <div className="msc-add-note-form">
                        <div className="msc-add-note-title">Add Assignment Note</div>

                        {/* Point selector */}
                        {jobDetail.diagram?.points?.length > 0 && (
                          <div className="md-field">
                            <label className="md-label">Diagram Point (optional)</label>
                            <select
                              className="md-input"
                              value={notePointId}
                              onChange={e => setNotePointId(e.target.value)}
                              style={{ cursor: 'pointer' }}
                            >
                              <option value="">General note (no specific point)</option>
                              {jobDetail.diagram.points.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Annotation type */}
                        <div className="md-field">
                          <label className="md-label">Note Type</label>
                          <div className="msc-note-type-grid">
                            {annotTypes.map(t => (
                              <button
                                key={t.id}
                                className={`msc-note-type-btn${noteTypeId === String(t.id) ? ' selected' : ''}`}
                                style={{
                                  background: `${t.color}14`,
                                  color: t.color,
                                  borderColor: `${t.color}33`,
                                }}
                                onClick={() => setNoteTypeId(String(t.id))}
                              >
                                <span style={{
                                  width: 7, height: 7, borderRadius: '50%',
                                  background: t.color, flexShrink: 0,
                                }}/>
                                {t.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Note text */}
                        <div className="md-field" style={{ marginBottom: 10 }}>
                          <label className="md-label">Note *</label>
                          <textarea
                            className="md-input"
                            placeholder="Describe what needs attention…"
                            rows={3}
                            value={noteText}
                            onChange={e => setNoteText(e.target.value)}
                            style={{ resize: 'vertical' }}
                          />
                        </div>

                        {noteError && (
                          <div className="md-alert md-alert-error" style={{ marginBottom: 10 }}>
                            ⚠ {noteError}
                          </div>
                        )}

                        <button
                          className="md-btn md-btn-primary"
                          style={{ width: '100%', justifyContent: 'center' }}
                          onClick={handleAddNote}
                          disabled={addingNote}
                        >
                          {addingNote
                            ? <><span className="md-spinner"/> Adding…</>
                            : <><Plus size={13}/> Add Note</>
                          }
                        </button>
                      </div>

                      {/* Diagram with pins (if exists) */}
                      {jobDetail.diagram && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
                            textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)',
                            fontFamily: 'DM Mono', marginBottom: 10,
                          }}>
                            Site Diagram
                          </div>
                          <div className="msc-diagram-wrap">
                            <img
                              src={`${BASE.replace('/api', '')}${jobDetail.diagram.diagram_url}`}
                              alt="Site diagram"
                              className="msc-diagram-img"
                            />
                            {jobDetail.diagram.points?.map((pin, idx) => {
                              const hasNote = jobDetail.notes?.some(n => n.point_id === pin.id);
                              return (
                                <div
                                  key={pin.id}
                                  className="msc-note-pin"
                                  style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%` }}
                                  onClick={() => setNotePointId(String(pin.id))}
                                >
                                  <div className={`msc-note-pin-dot ${hasNote ? 'msc-note-pin-has-note' : 'msc-note-pin-no-note'}`}>
                                    {idx + 1}
                                  </div>
                                  <div className="msc-note-pin-tooltip">{pin.label}</div>
                                </div>
                              );
                            })}
                          </div>
                          <div style={{
                            fontSize: 10, color: 'rgba(255,255,255,0.22)',
                            fontFamily: 'DM Mono', textAlign: 'center', marginTop: 8,
                          }}>
                            Gold pins have notes · Click a pin to select it
                          </div>
                        </div>
                      )}

                      {/* Existing notes */}
                      <div>
                        <div style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
                          textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)',
                          fontFamily: 'DM Mono', marginBottom: 10,
                        }}>
                          Notes ({jobDetail.notes?.length || 0})
                        </div>

                        {!jobDetail.notes?.length ? (
                          <div className="md-empty" style={{ padding: '24px 0' }}>
                            No assignment notes yet
                          </div>
                        ) : jobDetail.notes.map(note => (
                          <div key={note.id} className="msc-note-item">
                            <div className="msc-note-item-header">
                              <div
                                className="msc-note-type-dot"
                                style={{ background: note.annotation_type_color || '#94A3B8' }}
                              />
                              <span className="msc-note-point-label">
                                {note.point_label || 'General Note'}
                              </span>
                              {note.annotation_type_label && (
                                <span className="md-badge" style={{
                                  background: `${note.annotation_type_color}18`,
                                  color: note.annotation_type_color,
                                  border: `1px solid ${note.annotation_type_color}33`,
                                  fontSize: 9, padding: '2px 7px',
                                }}>
                                  {note.annotation_type_label}
                                </span>
                              )}
                              <button
                                className="md-btn md-btn-danger"
                                style={{ padding: '3px 6px', marginLeft: 'auto' }}
                                onClick={() => handleDeleteNote(note.id)}
                              >
                                <X size={10}/>
                              </button>
                            </div>
                            <div className="msc-note-text">{note.note}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* ── Create Job Modal ───────────────────────────────────── */}
      {showCreate && (
        <div className="md-modal-overlay">
          <div className="md-modal">
            <div className="md-modal-header">
              <CalendarDays size={16} color="rgba(14,165,233,0.70)" strokeWidth={1.8}/>
              <div className="md-modal-title">Schedule Job</div>
              <button className="md-modal-close" onClick={() => setShowCreate(false)}>
                <X size={18}/>
              </button>
            </div>
            <div className="md-modal-body">
              {createError && (
                <div className="md-alert md-alert-error">⚠ {createError}</div>
              )}
              <div className="md-grid-2">
                <div className="md-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="md-label">Date *</label>
                  <input
                    type="date"
                    className="md-input"
                    value={createDate}
                    onChange={e => setCreateDate(e.target.value)}
                  />
                </div>
                <div className="md-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="md-label">Site *</label>
                  <select
                    className="md-input"
                    value={createForm.site_id}
                    onChange={e => setCreateForm(f => ({ ...f, site_id: e.target.value }))}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="">Select site…</option>
                    {sites.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.site_name} — {s.client_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="md-label">Team</label>
                  <select
                    className="md-input"
                    value={createForm.team_id}
                    onChange={e => setCreateForm(f => ({ ...f, team_id: e.target.value }))}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="">Assign team later</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.team_name}</option>
                    ))}
                  </select>
                </div>
                <div className="md-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="md-label">Time</label>
                  <input
                    type="time"
                    className="md-input"
                    value={createForm.scheduled_time}
                    onChange={e => setCreateForm(f => ({ ...f, scheduled_time: e.target.value }))}
                  />
                </div>
                <div className="md-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="md-label">Notes</label>
                  <textarea
                    className="md-input"
                    placeholder="Any scheduling notes…"
                    rows={3}
                    value={createForm.notes}
                    onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>
            <div className="md-modal-footer">
              <button
                className="md-btn md-btn-ghost"
                onClick={() => setShowCreate(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                className="md-btn md-btn-primary"
                onClick={handleCreate}
                disabled={creating}
              >
                {creating
                  ? <><span className="md-spinner"/> Scheduling…</>
                  : 'Schedule Job'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MideastSchedule;